/* eslint-disable max-len */
import {
  Component,
  Element,
  Event,
  EventEmitter,
  h,
  Host,
  Prop,
  State,
  Listen,
  Watch,
  Method,
} from '@stencil/core';
import debounce from 'debounce';
import { versionComponent } from '../../global/inject-dependencies';
import { UiInputChangeEvent } from '../ui-input/ui-input';

interface UiAutosuggestChangeEvent {
  value: string;
  error?: boolean;
  shouldValidate?: boolean;
}

@Component({
  tag: 'ui-autosuggest',
  styleUrl: 'ui-autosuggest.scss',
  shadow: true,
})
export class UiAutosuggest {
  @Element() el: HTMLUiAutosuggestElement;

  /**
   * String that represents the user's input into the search area
   */
  @Prop() value: string;

  /**
   * Phrase that appears in the search bar before any input is given
   */
  @Prop() placeholder: string;

  @Prop() icon: string;

  /**
   * Initially sets the keyboard focus to the input element.
   */
  @Prop() inputInFocus = false;

  /**
   * Label for input
   */
  @Prop() label: string;

  /**
   * Boolean that check if the input value exists on suggestions
   */
  @Prop() validateOnSuggestions: boolean;
  // TODO: validateOnSuggestions might be misleading, hinting at listening for suggestion events.
  // TODO: rename to ...
  // validateAgainstSuggestions .. validate-against-suggestions
  // limitSearchToSuggestions .. limit-search-to-suggestions
  // limitQueryToSuggestions .. limit-query-to-suggestions
  // restrictQueryToSuggestions .. restict-query-to-suggestions
  // requireQueryToBeSuggestion .. require-query-to-be-suggestion
  // requireQueryMatchesSuggestion .. require-query-matches-suggestion
  // requireSuggestion .. require-suggestion
  // forcePickSuggestion .. force-pick-suggestion
  // queryHasToBeSuggestion .. query-has-to-be-suggestion

  /**
   * Boolean that check if suggestions should appear if is search value is not empty
   */
  @Prop() showOnFocus: boolean;

  /**
   * Phrase that appears bellow the input if the value doesn't exists on suggestions
   */
  @Prop() message?: string;

  /**
   * Boolean that checks if the input is set automatically instead when a suggestion is clicked
   * This is e. g. needed for the funds selection in ui-download-search, for the following two reasons:
   * - after selecting a fund, the auto-suggest value should still be empty
   * - the "value" includes HTML content that is not valid in the input
   * @default false
   * */
  @Prop() setSearchValueAutomatically? = true;

  /**
   * Boolean that checks whether there was an error with the search or not.
   */
  @Prop({ mutable: true }) error?: boolean = false;

  /**
   * Boolean that checks if should filter based on the input value.
   */
  @Prop({ mutable: true }) autoFilter?: boolean = false;

  /**
   * Event that is triggered everytime there is a change on the user's input into the search bar.
   */
  @Event({ eventName: 'uiAutosuggestChange' })
  change: EventEmitter<UiAutosuggestChangeEvent>;

  /**
   * Event that is triggered everytime there is a submit/search on the input.
   */
  @Event({ eventName: 'uiAutosuggestSubmit' })
  submit: EventEmitter<UiAutosuggestChangeEvent>;

  @State() open: boolean;

  /**
   * String that represents the current value of an input. Can be set programmatically or via user input.
   */
  @Prop({ mutable: true, reflect: true }) searchValue = '';

  @State() focusedSuggestionIndex: number;

  @State() allSuggestionsCache: HTMLUiSuggestionElement[] = [];

  @State() isFocused = false;

  suggestionsWrapperElement: HTMLElement;

  childObserver: MutationObserver = new MutationObserver(
    this.onChildrenChange.bind(this)
  );

  /**
   * Number of elements after the first element of the array to start scrolling down
   */
  startScrollingDown = 6;

  /**
   * Number of elements before the last element of the array to start scrolling up
   */
  startScrollingUp = 7;

  closedAfterSubmit = false;

  debouncedChangeEvent = debounce((value) =>
    this.change.emit({ value, error: true })
  );

  get activeSuggestion(): HTMLUiSuggestionElement {
    return this.filteredSuggestions[this.focusedSuggestionIndex];
  }

  get allSuggestions(): HTMLUiSuggestionElement[] {
    return Array.from(this.el.querySelectorAll('.ui-suggestion'));
  }

  get filteredSuggestions(): HTMLUiSuggestionElement[] {
    if (this.autoFilter) {
      return this.allSuggestionsCache.filter(
        (suggestion) =>
          suggestion.value
            .toLowerCase()
            .indexOf(this.searchValue.toLowerCase()) !== -1
      );
    }
    return this.allSuggestionsCache;
  }

  componentDidLoad() {
    this.childObserver.observe(this.el, {
      childList: true,
      subtree: true,
      // When the observer detects a change in ui-suggestion element count, it still takes some
      // time for versionComponent to add the component class .ui-suggestion. Observe attributes
      // and characterData to detect the class being added, so allSuggestions() can reliably (!)
      // retrieve elements by their class name.
      attributes: true,
      characterData: true,
    });
    this.onChildrenChange();
  }

  // TO-DO: Rename Function
  onChildrenChange() {
    this.allSuggestionsCache = this.allSuggestions;
    this.open =
      this.isFocused &&
      !this.closedAfterSubmit &&
      this.allSuggestionsCache.length > 0;
  }

  onInputChange(event: CustomEvent<UiInputChangeEvent>) {
    event.stopPropagation();
    this.closedAfterSubmit = false;
    this.searchValue = event.detail?.value;

    // It would be better for users to throttle the event, instead of debouncing it.
    this.debouncedChangeEvent(event.detail?.value);

    if (this.suggestionsWrapperElement?.scrollTop > 0) {
      this.suggestionsWrapperElement.scroll(0, 0);
    }

    // Hide suggestions
    if (!this.searchValue && !this.showOnFocus) {
      this.open = false;
    }
  }

  onInputFocus() {
    this.error = false;
    this.closedAfterSubmit = false;
    this.isFocused = true;
    if (this.showOnFocus) {
      this.open = true;
    }
  }

  showErrorMessageSuggestions() {
    if (this.validateOnSuggestions && this.searchValue?.length) {
      const searchValue = this.searchValue.toLocaleLowerCase();
      this.error = !this.filteredSuggestions.some(
        (suggestion: HTMLUiSuggestionElement) =>
          searchValue === suggestion.innerText.toLocaleLowerCase()
      );
      this.change.emit({
        value: this.searchValue,
        error: this.error,
        shouldValidate: true,
      });
    }
  }

  onKeyUp(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        this.onArrowDown();
        break;
      case 'ArrowUp':
        this.onArrowUp();
        break;
      case 'Enter':
        this.onSubmit();
        break;
      case 'Escape':
        this.clearInput();
        break;
      default:
        // Otherwise, the error is not cleared if you never click inside the input box again
        this.error = false;
        break;
    }
  }

  onArrowUp() {
    if (this.focusedSuggestionIndex !== 0) {
      this.focusedSuggestionIndex -= 1;
    }

    if (
      this.focusedSuggestionIndex <
      this.filteredSuggestions.length - this.startScrollingUp
    ) {
      this.scrollContainer('up');
    }
  }

  onArrowDown() {
    if (this.focusedSuggestionIndex === undefined) {
      this.focusedSuggestionIndex = 0;
      return;
    }

    if (this.focusedSuggestionIndex < this.filteredSuggestions.length - 1) {
      this.focusedSuggestionIndex += 1;
    }

    if (this.focusedSuggestionIndex > this.startScrollingDown) {
      this.scrollContainer('down');
    }
  }

  @Watch('focusedSuggestionIndex')
  onFocusChange() {
    this.allSuggestionsCache.forEach((child: HTMLUiSuggestionElement) => {
      // eslint-disable-next-line no-param-reassign
      child.focused = false;
    });

    if (this.focusedSuggestionIndex >= 0) {
      this.filteredSuggestions[this.focusedSuggestionIndex].focused = true;
    }
  }

  scrollContainer(direction: 'up' | 'down') {
    const scrollPosition = this.suggestionsWrapperElement?.scrollTop;
    if (direction === 'up') {
      this.suggestionsWrapperElement.scroll(
        0,
        scrollPosition -
          this.el.querySelector('.ui-suggestion').getBoundingClientRect().height
      );
    } else {
      this.suggestionsWrapperElement.scroll(
        0,
        scrollPosition +
          this.el.querySelector('.ui-suggestion').getBoundingClientRect().height
      );
    }
  }

  onSubmit() {
    this.open = false;
    this.closedAfterSubmit = true;
    let isError: boolean;

    if (this.activeSuggestion) {
      if (this.setSearchValueAutomatically)
        this.searchValue = this.activeSuggestion.value;
      isError = this.searchValue === this.activeSuggestion?.value;
    } else {
      isError = !!this.validateOnSuggestions;
    }

    this.submit.emit({
      value: this.searchValue,
      shouldValidate: true,
      error: isError,
    });

    this.showErrorMessageSuggestions();
  }

  @Listen('blur')
  onBlur() {
    this.isFocused = false;
  }

  @Listen('inputIconClicked')
  onInputIconClicked() {
    /**
     * The following lets the auto-suggest open when it's used as a dropdown
     * This is needed for the download-search in funds-mode
     */
    if (this.icon === 'chevron-bottom') {
      return;
    }
    this.open = false;
  }

  @Listen('uiSuggestionClick')
  onSuggestionClick(event: CustomEvent) {
    if (this.filteredSuggestions?.length === 0) {
      this.onChildrenChange();
    }
    this.focusedSuggestionIndex = this.filteredSuggestions.indexOf(
      event.target as HTMLUiSuggestionElement
    );
    this.onSubmit();
  }

  /**
   * This will clear the input field and reset the value.
   */
  @Method()
  async clearInput() {
    this.searchValue = '';
    this.open = false;
    this.error = false;
  }

  @Listen('click', { target: 'window' })
  closeSuggestionsOnClickOutside(event: MouseEvent) {
    if (!this.open) {
      return;
    }
    if (
      this.el.contains(event.target as HTMLElement) ||
      this.el === event.target
    ) {
      return;
    }
    this.showErrorMessageSuggestions();
    this.open = false;
  }

  @Watch('searchValue')
  onSearchValueChange() {
    if (this.searchValue?.length) {
      this.allSuggestionsCache.forEach((suggestion) => {
        // eslint-disable-next-line no-param-reassign
        suggestion.disabled = !this.filteredSuggestions.includes(suggestion);
      });
    } else {
      this.allSuggestionsCache.forEach((suggestion) => {
        // eslint-disable-next-line no-param-reassign
        suggestion.disabled = false;
      });
    }
    this.focusedSuggestionIndex = undefined;
  }

  @Watch('value')
  onControlledValueChange() {
    this.searchValue = this.value;
  }

  render() {
    return (
      <Host>
        <div
          class={{
            'suggestions-container': true,
            active: this.open,
            hasLabel: !!this.label,
          }}
        >
          <div
            class="suggestions"
            part="dropdown"
            ref={(el) => {
              this.suggestionsWrapperElement = el;
            }}
          >
            <slot></slot>
          </div>
        </div>
        {this.label && (
          <label class="suggestions-container--label">{this.label}</label>
        )}
        <ui-input
          clearable
          placeholder={this.placeholder}
          icon={this.icon}
          value={this.searchValue}
          onUiInputChange={this.onInputChange.bind(this)}
          onUiInputFocus={this.onInputFocus.bind(this)}
          onKeyUp={this.onKeyUp.bind(this)}
          focus={this.inputInFocus}
          error={this.error}
          message={this.error === true ? this.message : ''}
        ></ui-input>
      </Host>
    );
  }
}

versionComponent(UiAutosuggest);
